/**
 * Drive — modal do editor OnlyOffice (Story E14.2.3).
 *
 * Quase-fullscreen (95vw/95vh). Busca a sessão via `useEditorSession` e decide
 * uma de quatro views:
 *  - `loading`        — uma busca em voo e ainda sem sessão (inicial OU retry).
 *  - `session-error`  — falha ao abrir a sessão (503 ambiente sem editor, 403
 *                       sem permissão, 404 inexistente, genérico c/ retry).
 *  - `editor-error`   — sessão OK mas o editor falhou (api.js não carregou,
 *                       throw no render, ou config 200-porém-malformado) →
 *                       "Falha ao carregar o editor" (linha da I/O Matrix).
 *  - `editor`         — carrega `OnlyOfficeIframe` via `React.lazy` dentro de um
 *                       ErrorBoundary (lazy reject / throw não derruba o app).
 *
 * Salvar é server-side: o Document Server chama o `/callback` (E14.2.2) no
 * autosave/fechar — o frontend NÃO faz upload do binário.
 */
import { Component, ReactNode, Suspense, lazy, useEffect, useState } from "react";
import { AlertCircle, Loader2, MessageSquare } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useEditorSession } from "@/hooks/useEditorSession";
import { editorErrorMessage, isTerminalEditorError } from "@/lib/onlyoffice-iframe-wrapper";
import type { ModoDoEditor } from "@/services/onlyofficeApi";

const OnlyOfficeIframe = lazy(() => import("@/components/drive/OnlyOfficeIframe"));
const DocumentCommentsPanel = lazy(() =>
  import("@/components/drive/DocumentCommentsPanel").then((m) => ({
    default: m.DocumentCommentsPanel,
  }))
);

interface DocumentEditorDialogProps {
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentName?: string;
  /** `visualizar` abre só para ler, também para quem pode editar. Padrão: `editar`. */
  modo?: ModoDoEditor;
}

/**
 * Captura falhas de RENDER do editor (incl. rejeição do `React.lazy`, ex.: chunk
 * stale / CSP bloqueando o bundle do pacote) — sem isso a exceção sobe e
 * white-screena o app inteiro (não há ErrorBoundary acima da rota). Ao capturar,
 * notifica o pai (que troca pra view `editor-error`) e para de renderizar o
 * filho (evita loop de re-throw).
 */
export class EditorErrorBoundary extends Component<
  { onError: (error: unknown) => void; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    this.props.onError(error);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

function LoadingBlock({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      {label}
    </div>
  );
}

function ErrorBlock({
  message,
  onRetry,
  onClose,
}: {
  message: string;
  onRetry?: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
      <AlertCircle className="h-7 w-7 text-destructive" />
      <span>{message}</span>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={onClose}>
        Fechar
      </Button>
    </div>
  );
}

export function DocumentEditorDialog({
  documentId,
  open,
  onOpenChange,
  documentName,
  modo = "editar",
}: DocumentEditorDialogProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [editorError, setEditorError] = useState<unknown>(null);
  // No celular os comentários nascem fechados: o painel tem 340 px fixos e deixava o documento
  // com ~30 px (visto no "Visualizar" pelo toque no cartão, 23/10/2026). O botão abre.
  const [showComments, setShowComments] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 768
  );
  const {
    data: session,
    isError,
    isFetching,
    error,
    refetch,
  } = useEditorSession(documentId, open, modo);

  // Reseta o estado efêmero ao fechar / trocar de doc — o componente não pode
  // carregar `isDirty`/erro stale pra próxima abertura (defesa além do
  // render-condicional do DocumentBrowser, que já remonta por editorDocId).
  useEffect(() => {
    if (!open) {
      setIsDirty(false);
      setEditorError(null);
    }
  }, [open]);
  useEffect(() => {
    setEditorError(null);
  }, [documentId]);

  // Guarda: fechar com alterações ainda em voo pode perder o último autosave (o
  // save é assíncrono via callback do DS). Confirma antes de fechar. TODOS os
  // caminhos de fechar (X/ESC/overlay do Radix e os botões "Fechar") passam aqui.
  const requestClose = (next: boolean) => {
    if (!next && isDirty) {
      const ok = window.confirm("Há alterações ainda sendo salvas. Fechar o editor mesmo assim?");
      if (!ok) return;
    }
    onOpenChange(next);
  };

  // Sessão 200 mas corpo inválido (sem URL do DS ou sem config) → trata como
  // falha do editor em vez de montar um `DocumentEditor` quebrado (que poderia
  // dar throw e cair no ErrorBoundary de qualquer forma).
  const sessionInvalid = !!session && (!session.document_server_url || !session.config);
  const editorFailed = editorError !== null || sessionInvalid;

  let view: "loading" | "session-error" | "editor-error" | "editor";
  if (isError && !isFetching) view = "session-error";
  else if (session && editorFailed) view = "editor-error";
  else if (session && !isFetching) view = "editor";
  else view = "loading";

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="flex h-[95vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="flex-row items-center justify-between border-b px-4 py-2">
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm">
              {documentName
                ? `${modo === "visualizar" ? "Visualizando" : "Editando"}: ${documentName}`
                : "Editor de documento"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Editor de documentos OnlyOffice incorporado.
            </DialogDescription>
          </div>
          {view === "editor" && (
            <Button
              variant={showComments ? "secondary" : "ghost"}
              size="sm"
              className="h-7 shrink-0"
              onClick={() => setShowComments((v) => !v)}
              aria-pressed={showComments}
            >
              <MessageSquare className="mr-1 h-4 w-4" />
              Comentários
            </Button>
          )}
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          <div className="min-h-0 min-w-0 flex-1">
            {view === "loading" && <LoadingBlock label="Abrindo o editor…" />}

            {view === "session-error" && (
              <ErrorBlock
                message={editorErrorMessage(error)}
                onRetry={isTerminalEditorError(error) ? undefined : () => refetch()}
                onClose={() => requestClose(false)}
              />
            )}

            {view === "editor-error" && (
              <ErrorBlock
                message="Falha ao carregar o editor."
                onClose={() => requestClose(false)}
              />
            )}

            {view === "editor" && session && (
              <EditorErrorBoundary onError={setEditorError}>
                <Suspense fallback={<LoadingBlock label="Carregando editor…" />}>
                  <OnlyOfficeIframe
                    session={session}
                    documentId={documentId}
                    onStateChange={setIsDirty}
                    onError={setEditorError}
                  />
                </Suspense>
              </EditorErrorBoundary>
            )}
          </div>

          {view === "editor" && showComments && (
            <Suspense
              fallback={
                <div className="w-[340px] shrink-0 border-l">
                  <LoadingBlock label="Carregando comentários…" />
                </div>
              }
            >
              <DocumentCommentsPanel documentId={documentId} />
            </Suspense>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
